import Constant from '@/const'
import Store from '@/store/index.js'
import moment from 'moment'

/**
 * 見積り関連の共通処理を提供するMixin
 */
export default {
  methods: {
    /**
     * 登録場所種別を返却
     * @param mitumori 見積
     * @param hikino HIKITORI or NOSYA
     */
    getMitumoriSyubetu(mitumori, hikino) {
      if (mitumori[`${hikino}_AA_CD`] === undefined) return null
      else if (mitumori[`${hikino}_FAVORITE_ID`]) return 'FAVORITE'
      else if (mitumori[`${hikino}_AA_CD`]) return 'AUCTION'
      else if (mitumori[`${hikino}_TOROKU_ID`]) return 'MASTER'
      else if (mitumori[`${hikino}_HIKINO_CD`]) return 'HIKINO'
      else return 'DIRECT'
    },

    /**
     * 見積りが有効期限切れか判定
     * @param {Object} mitumori 見積データ
     * @returns {Boolean} 期限切れの場合true
     */
    isMitumoriExpired(mitumori) {
      if (!mitumori.LIMITED_NICHIJI) return false
      const today = moment().format('YYYY-MM-DD')
      const limit = moment(mitumori.LIMITED_NICHIJI).format('YYYY-MM-DD')
      return moment(today).isAfter(limit)
    },

    /**
     * 見積データから引納先データを作成
     * @param {Object} mitumori 見積データ
     * @param {String} hikino 'HIKITORI' or 'NOSYA'
     * @returns {Object} 引納先データ
     */
    createHikinoDataFromMitumori(mitumori, hikino) {
      const aaCd = mitumori[`${hikino}_AA_CD`] || null
      return {
        TOROKUBAYO_KO_ID: mitumori[`${hikino}_TOROKU_ID`],
        AA_CD: aaCd,
        YUBIN1: mitumori[`${hikino}_YUBIN1`],
        YUBIN2: mitumori[`${hikino}_YUBIN2`],
        TODO_CD: mitumori[`${hikino}_TODO_CD`],
        TODO_MEI: mitumori[`${hikino}_TODO_MEI`],
        SHIKI_CD: aaCd,
        SHIKU_CD: mitumori[`${hikino}_SHIKU_CD`],
        SHIKU_MEI: mitumori[`${hikino}_SHIKU_MEI`],
        BANCHI: mitumori[`${hikino}_BANCHI`],
        MEI: mitumori[`${hikino}_MEI`],
        TEL: mitumori[`${hikino}_TEL`],
        IS_PERSONAL: mitumori[`${hikino}_KOJIN_FLG`],
        FAVORITE_ID: mitumori[`${hikino}_FAVORITE_ID`],
        YUSO_KBN: mitumori[`${hikino}_KOJIN_FLG`]
          ? Constant.YUSOKBN.KOJIN
          : null,
      }
    },

    /**
     * 見積データから車両データを作成（trial画面用）
     * @param {Object} mitumori 見積データ
     * @param {Array} rikujiList 陸自リスト（オプション）
     * @returns {Object} 車両データ
     */
    createSyaryoDataFromMitumori(mitumori, rikujiList = null) {
      const syaryo = {
        SYARYO_TYPE_KBN: mitumori.MEIHEN_FLG
          ? mitumori.SYARYO_TYPE_KBN === Constant.syaryoTypeKbn.kei
            ? Constant.syaryoTypeKbn.kei
            : Constant.syaryoTypeKbn.normal
          : mitumori.SYARYO_TYPE_KBN,
        SYARYO_MEI: mitumori.SYARYO_MEI,
        MEIHEN_KEI_FLG: mitumori.MEIHEN_FLG
          ? Number(mitumori.SYARYO_TYPE_KBN === Constant.syaryoTypeKbn.kei)
          : 0,
        JISOU_KBN: mitumori.JISOU_KBN,
        JYOTAI_FLG: mitumori.NORMAL_FLG ? 0 : 1,
        RIKUJI_MEI: mitumori.RIKUJI_MEI,
        SYARYO_NAGASA: mitumori.NAGASA,
        SYARYO_HABA: mitumori.HABA,
        SYARYO_TAKASA: mitumori.TAKASA,
        SEISOU_FLG: mitumori.SEISOU_FLG,
        SEISOU_KBN: mitumori.SEISOU_KBN,
        HOKAN_FLG: mitumori.HOKAN_FLG,
        ETC_SETUP_FLG: mitumori.ETC_SETUP_FLG,
        SYAKOSYO_JYURYO_FLG: mitumori.SYAKOSYO_JYURYO_FLG,
        MEIHEN_KIBO_NUMBER_FLG: mitumori.KIBO_NUMBER_FLG,
        MEIHEN_KIBO_NUMBER_DAIKO_FLG: mitumori.MEIHEN_KIBO_NUMBER_DAIKO_FLG,
        MEIHEN_UNYU_SIKYOKU_SHIKU_CD: null,
      }

      // 陸自リストが渡されている場合、名称からコードを設定
      if (rikujiList && syaryo.RIKUJI_MEI) {
        this.setRikujiCodeFromName(syaryo, rikujiList)
      }

      return syaryo
    },

    /**
     * 陸自名称からコードを取得して設定する
     * @param {Object} syaryo 車両データ
     * @param {Array} rikujiList 陸自リスト（二重配列: rikujiList[MEIHEN_KEI_FLG]）
     * @returns {Object} コードが設定された車両データ
     */
    setRikujiCodeFromName(syaryo, rikujiList) {
      if (!syaryo.RIKUJI_MEI || !rikujiList) {
        return syaryo
      }

      const keiFlg = Number(syaryo.MEIHEN_KEI_FLG || 0)
      const list = rikujiList[keiFlg]

      if (!list) {
        return syaryo
      }

      // RIKUJI_MEI（名称）からコードを逆引き
      const rikuji = list.find(
        (item) =>
          item.RIKUJI_RYAKUSYO === syaryo.RIKUJI_MEI ||
          item.RIKUJI_SEISYO === syaryo.RIKUJI_MEI
      )

      if (rikuji) {
        syaryo.MEIHEN_UNYU_SIKYOKU_SHIKU_CD = rikuji.SHIKU_CD
      }

      return syaryo
    },

    /**
     * FAVORITEデータのPLACE_TYPEとSHIKI_CDを設定する
     * @param {Object} favoriteData FAVORITEデータ
     * @returns {Object} PLACE_TYPEとSHIKI_CDが設定されたFAVORITEデータ
     */
    setFavoritePlaceType(favoriteData) {
      if (!favoriteData) {
        return favoriteData
      }

      // AA_IDがあればAUCTION、なければAREA
      const isAAFlg =
        Number(favoriteData.AA_ID) || Number(favoriteData.AA_CD) ? 1 : 0
      const typeName = isAAFlg ? 'AUCTION' : 'AREA'

      favoriteData.PLACE_TYPE = Constant.RYOKIN_API_AREA_TYPE[typeName]
      favoriteData.SHIKI_CD = favoriteData.AA_CD

      return favoriteData
    },

    /**
     * 見積から発注する
     * @param  {Number} id 見積ID
     */
    async mitumoriOrder(id) {
      let mitumori = null

      const fields = {
        mitumori: '*',
        mitumori_uchiwake: ['UCHIWAKE_NAME', 'UCHIWAKE_PRICE'],
      }

      await Store.dispatch('mitumori/find', {
        cond: { ID: id },
        fields: fields,
      }).then((list) => {
        mitumori = list[0]
        // 履歴テーブルとフロント定義の項目の差異を埋める
        mitumori.SYARYO_NAGASA = mitumori.NAGASA
        mitumori.SYARYO_TAKASA = mitumori.TAKASA
        mitumori.SYARYO_HABA = mitumori.HABA
        mitumori.JYOTAI_FLG = mitumori.NORMAL_FLG ? 0 : 1
        mitumori.MEIHEN_KIBO_NUMBER_FLG = mitumori.KIBO_NUMBER_FLG
        mitumori.MEIHEN_KEI_FLG = mitumori.MEIHEN_FLG
          ? Number(mitumori.SYARYO_TYPE_KBN === Constant.syaryoTypeKbn.kei)
          : null
        mitumori.SYARYO_TYPE_KBN = mitumori.MEIHEN_KEI_FLG
          ? Constant.syaryoTypeKbn.normal
          : mitumori.SYARYO_TYPE_KBN
      })

      /**
       * 発注タブ種別を返却
       * @param hikinoType HIKITORI or NOSYA
       * @returns 発注タブ種別
       */
      const getActiveTab = (hikinoType) => {
        let kbn = this.getMitumoriSyubetu(mitumori, hikinoType)
        if (!kbn) {
          throw new Error('発注できない見積です')
        }

        // ハブで引納先マスタから選択した場合は、直接入力に変換する
        if (kbn === 'HIKINO') {
          return 'DIRECT'
        }

        return kbn
      }

      const isAuction = (kbn) => Number(kbn === 'AUCTION')

      const hKbn = getActiveTab('HIKITORI')
      const nKbn = getActiveTab('NOSYA')

      let order = {
        isFromTrialEdit: true,
        ACTIVE_HIKITORI_TAB: hKbn,
        ACTIVE_NOSYA_TAB: nKbn,
        MEIHEN_FLG: mitumori.MEIHEN_FLG,
        HIKITORI_IS_MASTER_ACTIVE: false,
        NOSYA_IS_MASTER_ACTIVE: false,
        HIKITORI_AA_FLG: isAuction(hKbn),
        NOSYA_AA_FLG: isAuction(nKbn),
        mitumori: mitumori,
        syaryos: [],
      }

      const items = [
        'SYARYO_TYPE_KBN',
        'SYARYO_MEI',
        'SYARYO_NAGASA',
        'SYARYO_HABA',
        'SYARYO_TAKASA',
        'SEISOU_FLG',
        'SEISOU_KBN',
        'SYARYO_TYPE_KBN',
        'MEIHEN_KEI_FLG',
        'JYOTAI_FLG',
        'RIKUJI_MEI',
        'JISOU_KBN',
        'HOKAN_FLG',
        'ETC_SETUP_FLG',
        'SYAKOSYO_JYURYO_FLG',
        'MEIHEN_KIBO_NUMBER_FLG',
        'MEIHEN_KIBO_NUMBER_DAIKO_FLG',
      ]
      let syaryo = { isMitumori: true }
      for (var name of items) {
        if (mitumori[name] !== null) syaryo[name] = mitumori[name]
      }
      order.syaryos.push(syaryo)

      order[`HIKITORI_IS_${hKbn}_ACTIVE`] = true
      order[`NOSYA_IS_${nKbn}_ACTIVE`] = true
      order[`HIKITORI_${hKbn}`] = this.createHikinoDataFromMitumori(
        mitumori,
        'HIKITORI'
      )
      order[`NOSYA_${nKbn}`] = this.createHikinoDataFromMitumori(
        mitumori,
        'NOSYA'
      )

      this.$router.push({
        name: 'OrderTransport',
        params: { yusoKbn: mitumori.MEIHEN_FLG, form: order },
      })
    },
  },
}



// WEBPACK FOOTER //
// ./src/components/mixin/MitumoriMixin.js